import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { taskApi } from '../api/endpoints.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { useApi } from '../hooks/useApi.js';
import { useToast } from '../hooks/useToast.jsx';
import { useApiError } from '../hooks/useApiError.js';
import PageHeader from '../components/PageHeader.jsx';
import Toolbar from '../components/Toolbar.jsx';
import Modal from '../components/Modal.jsx';
import Spinner from '../components/Spinner.jsx';
import Badge from '../components/Badge.jsx';
import { Field, Select, TextInput } from '../components/Field.jsx';
import MonthCalendar, { MonthNav, firstOfMonth } from '../components/MonthCalendar.jsx';
import { formatDayLong, formatTime, isoDay, fullName } from '../utils/format.js';
import { TASK_PRIORITIES } from '../constants.js';

/**
 * A personal diary: your dated tasks, on a month grid.
 *
 * "Yours" is the same set the Tasks screen lists - assigned to you, or written
 * by you and handed to someone else - so a task can never show up in one place
 * and be missing from the other. A delegated one carries the assignee's name so
 * it does not read as your own work.
 *
 * It stays private by construction: the API is asked for `mine=true`, which
 * never widens to the agency, and another person's private task is invisible
 * even to their manager.
 *
 * Pick a day to see its hours, tick a task off, or drop a new one on that date.
 */

const PRIORITY_BADGE = { HIGH: 'HOT', NORMAL: 'WARM', LOW: 'COLD' };

const MyAgendaPage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const toMessage = useApiError();
  const locale = i18n.resolvedLanguage;

  const [cursor, setCursor] = useState(firstOfMonth());
  const [openOnly, setOpenOnly] = useState(true);
  const [day, setDay] = useState(null);
  const [draft, setDraft] = useState(null); // the "add on this day" form

  const { data, loading, refresh } = useApi(() => taskApi.list({ mine: true }), []);

  const tasks = data?.items ?? [];
  const shown = openOnly ? tasks.filter((task) => task.status === 'OPEN') : tasks;

  /** Dated tasks bucketed by day; undated ones are counted separately. */
  const byDay = useMemo(() => {
    const map = new Map();
    for (const task of shown) {
      if (!task.dueAt) continue;
      const key = isoDay(task.dueAt);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(task);
    }
    for (const list of map.values()) list.sort((a, b) => (a.dueAt < b.dueAt ? -1 : 1));
    return map;
  }, [shown]);

  const undated = shown.filter((task) => !task.dueAt).length;
  const monthPrefix = isoDay(cursor).slice(0, 7);
  const monthCount = [...byDay.entries()]
    .filter(([key]) => key.startsWith(monthPrefix))
    .reduce((sum, [, list]) => sum + list.length, 0);

  const dayTasks = day ? (byDay.get(day) ?? []) : [];

  const chipClass = (task) =>
    [
      'rdv-chip',
      'rdv-chip--task',
      task.status === 'DONE' ? 'rdv-chip--done' : '',
      task.overdue ? 'rdv-chip--red' : task.priority === 'HIGH' ? 'rdv-chip--amber' : 'rdv-chip--violet',
    ]
      .filter(Boolean)
      .join(' ');

  const setStatus = async (task, status) => {
    try {
      await taskApi.update(task.id, { status });
      await refresh();
    } catch (err) {
      toast.error(toMessage(err));
    }
  };

  const addTask = async () => {
    try {
      await taskApi.create({
        title: draft.title,
        priority: draft.priority,
        dueAt: new Date(`${day}T${draft.time || '09:00'}:00.000Z`).toISOString(),
        // Added from the personal diary: private unless said otherwise.
        isPrivate: draft.isPrivate,
      });
      toast.success(t('common.save'));
      setDraft(null);
      await refresh();
    } catch (err) {
      toast.error(toMessage(err));
    }
  };

  const today = isoDay();

  /** The month cell: the day's tasks, hour first. */
  const renderDay = (key) => {
    const list = byDay.get(key);

    // An empty day still opens - that is how you drop a task on it - but only
    // from today on: there is nothing to schedule in the past. The hint stays
    // invisible until the cell is hovered or focused.
    if (!list?.length) {
      return key >= today ? <span className="calendar__cta">{t('tasks.newTask')}</span> : null;
    }

    // Plain spans, not buttons: the whole cell is the click target.
    return (
      <>
        {list.slice(0, 3).map((task) => (
          <span key={task.id} className={chipClass(task)} title={task.title}>
            <span className="rdv-chip__time">{formatTime(task.dueAt, locale)}</span>
            <span className="rdv-chip__label">{task.title}</span>
            {task.assignedTo !== user.id ? (
              <span className="rdv-chip__who">{task.assignee?.firstName}</span>
            ) : null}
          </span>
        ))}
        {list.length > 3 ? <span className="person person--more">+{list.length - 3}</span> : null}
        <span className="calendar__cta">{t('agenda.seeDay', { count: list.length })}</span>
      </>
    );
  };

  if (loading) return <Spinner full />;

  return (
    <>
      <PageHeader title={t('agenda.myAgenda')} subtitle={t('agenda.myAgendaHint')} />

      <Toolbar>
        <MonthNav cursor={cursor} onChange={setCursor} />
        <div className="segmented" role="group">
          {[true, false].map((value) => (
            <button
              key={String(value)}
              type="button"
              className={openOnly === value ? 'segmented__btn segmented__btn--on' : 'segmented__btn'}
              onClick={() => setOpenOnly(value)}
            >
              {value ? t('tasks.statuses.OPEN') : t('common.all')}
            </button>
          ))}
        </div>
        <span className="toolbar__count">
          {monthCount} {t('common.results')}
        </span>
      </Toolbar>

      <MonthCalendar cursor={cursor} renderDay={renderDay} onDayClick={(key) => setDay(key)} />

      {undated ? (
        <p className="muted empty-month">
          {t('agenda.undatedTasks', { count: undated })}{' '}
          <button type="button" className="link-button" onClick={() => navigate('/tasks')}>
            {t('tasks.title')}
          </button>
        </p>
      ) : null}

      <Modal
        open={Boolean(day)}
        title={day ? formatDayLong(day, locale) : ''}
        onClose={() => {
          setDay(null);
          setDraft(null);
        }}
        width={560}
        footer={
          draft ? (
            <>
              <button type="button" className="btn" onClick={() => setDraft(null)}>
                {t('common.cancel')}
              </button>
              <button type="button" className="btn btn--primary" onClick={addTask} disabled={!draft.title}>
                {t('common.save')}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => setDraft({ title: '', time: '09:00', priority: 'NORMAL', isPrivate: true })}
            >
              {t('tasks.newTask')}
            </button>
          )
        }
      >
        {!draft ? (
          <ul className="task-list">
            {dayTasks.map((task) => (
              <li key={task.id} className={task.overdue ? 'task task--overdue' : 'task'}>
                <input
                  type="checkbox"
                  checked={task.status === 'DONE'}
                  onChange={() => setStatus(task, task.status === 'DONE' ? 'OPEN' : 'DONE')}
                  aria-label={t('tasks.statuses.DONE')}
                />
                <div className="task__body">
                  {/* A callback task points back at the account it came from. */}
                  {task.customerId ? (
                    <button
                      type="button"
                      className="task__title"
                      onClick={() => navigate(`/customers/${task.customerId}`)}
                    >
                      {task.title}
                    </button>
                  ) : (
                    <span className="task__title task__title--plain">{task.title}</span>
                  )}
                  <span className="task__meta">
                    {formatTime(task.dueAt, locale)}
                    {task.customerName ? ` - ${task.customerName}` : ''}
                    {task.assignedTo !== user.id ? ` - ${t('tasks.delegatedTo', { name: fullName(task.assignee) })}` : ''}
                    {task.isPrivate ? ` - ${t('tasks.private')}` : ''}
                  </span>
                </div>
                <Badge value={PRIORITY_BADGE[task.priority]} label={t(`tasks.priorities.${task.priority}`)} />
              </li>
            ))}
            {dayTasks.length === 0 ? <li className="muted">{t('tasks.empty')}</li> : null}
          </ul>
        ) : (
          <div className="form-grid">
            <div className="span-2">
              <Field label={t('tasks.taskTitle')} htmlFor="d-title" required>
                <TextInput id="d-title" value={draft.title} onChange={(v) => setDraft({ ...draft, title: v })} />
              </Field>
            </div>
            <Field label={t('agenda.time')} htmlFor="d-time">
              <TextInput
                id="d-time"
                type="time"
                value={draft.time}
                onChange={(v) => setDraft({ ...draft, time: v })}
              />
            </Field>
            <Field label={t('tasks.priority')} htmlFor="d-prio">
              <Select
                id="d-prio"
                value={draft.priority}
                onChange={(v) => setDraft({ ...draft, priority: v })}
                options={TASK_PRIORITIES.map((p) => ({ value: p, label: t(`tasks.priorities.${p}`) }))}
              />
            </Field>
            <div className="span-2">
              <label className={draft.isPrivate ? 'option option--checked' : 'option'}>
                <input
                  type="checkbox"
                  checked={draft.isPrivate}
                  onChange={() => setDraft({ ...draft, isPrivate: !draft.isPrivate })}
                />
                <span>{t('tasks.private')}</span>
              </label>
              <p className="muted field__hint">{t('tasks.privateHint')}</p>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default MyAgendaPage;
